import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Download, Mail, Home, ShoppingBag } from 'lucide-react';
import { Order } from '../types';
import { formatPrice, formatDate } from '../utils/helpers';
import Button from '../components/ui/Button';
import { useOrderStore } from '../store/orderStore';

const OrderSuccess: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lastOrder, clearLastOrder } = useOrderStore();
  
  // Try to get order from navigation state first, then store, then localStorage as fallback
  let order: Order | null = location.state?.order || lastOrder || null;
  
  if (!order) {
    try {
      const storedOrder = localStorage.getItem('lastOrder');
      if (storedOrder) {
        order = JSON.parse(storedOrder);
        // Clean up localStorage after retrieving the order
        localStorage.removeItem('lastOrder');
      }
    } catch (error) {
      console.error('Error parsing stored order:', error);
    }
  }
  
  // Clear the order from store after retrieving it
  useEffect(() => {
    if (order) {
      clearLastOrder();
    }
  }, [order, clearLastOrder]);

  if (!order) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-red-600" />
          </div>
          <h1 className="text-2xl font-semibold text-secondary-900 mb-4">
            Order Not Found
          </h1>
          <p className="text-secondary-600 mb-6">
            We couldn't find the order details. Please contact us if you need assistance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/')}>
              Back to Home
            </Button>
            <Button variant="outline" onClick={() => navigate('/contact')}>
              Contact Us
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <div className="container-max section-padding">
        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-12 h-12 text-green-600" />
          </motion.div>
          
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Order Confirmed!
          </h1>
          
          <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
            Thank you for your order. We've received your request and will be in touch soon 
            to discuss the details and arrange payment.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Details */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">
                Order Details
              </h2>

              {/* Order Info */}
              <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Order ID:</span>
                  <span className="font-mono font-semibold text-secondary-900">{order.id}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Order Date:</span>
                  <span className="font-semibold text-secondary-900">{formatDate(order.createdAt)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Status:</span>
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                </div>
              </div>

              {/* Customer Info */}
              <div className="border-t border-secondary-200 pt-6 mb-8">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                  Customer Information
                </h3>
                <div className="space-y-2">
                  <p><span className="text-secondary-600">Name:</span> {order.userInfo.name}</p>
                  <p><span className="text-secondary-600">Email:</span> {order.userInfo.email}</p>
                  {order.userInfo.phone && (
                    <p><span className="text-secondary-600">Phone:</span> {order.userInfo.phone}</p>
                  )}
                  <p><span className="text-secondary-600">Address:</span> {order.userInfo.address}</p>
                  {order.userInfo.notes && (
                    <p><span className="text-secondary-600">Notes:</span> {order.userInfo.notes}</p>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div className="border-t border-secondary-200 pt-6">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                  Order Items
                </h3>
                <div className="space-y-4">
                  {order.items.map((item) => (
                    <div key={item.product.id} className="flex items-center space-x-4">
                      <img
                        src={item.product.images[0]}
                        alt={item.product.title}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-secondary-900">{item.product.title}</h4>
                        <p className="text-sm text-secondary-500">Qty: {item.quantity}</p>
                      </div>
                      <span className="font-semibold text-secondary-900">
                        {formatPrice(item.product.price * item.quantity)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Order Summary & Actions */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-6"
          >
            {/* Order Summary */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">
                Order Summary
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-secondary-600">Subtotal</span>
                  <span className="font-semibold">{formatPrice(order.total / 1.08)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-600">Tax</span>
                  <span className="font-semibold">{formatPrice(order.total - (order.total / 1.08))}</span>
                </div>
                <div className="border-t border-secondary-200 pt-3">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-secondary-900">Total</span>
                    <span className="text-lg font-bold text-primary-600">
                      {formatPrice(order.total)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">
                What's Next?
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-primary-600 font-semibold text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-900">Order Review</h4>
                    <p className="text-sm text-secondary-600">We'll review your order and contact you within 24 hours.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-primary-600 font-semibold text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-900">Payment & Details</h4>
                    <p className="text-sm text-secondary-600">We'll discuss payment options and finalize your custom requirements.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-primary-600 font-semibold text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-900">Production & Delivery</h4>
                    <p className="text-sm text-secondary-600">Your custom calligraphy will be created and delivered to you.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="space-y-4">
                <Button
                  onClick={() => window.print()}
                  variant="outline"
                  className="w-full"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Receipt
                </Button>
                
                <Button
                  onClick={() => navigate('/contact')}
                  variant="outline"
                  className="w-full"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Contact Support
                </Button>
                
                <div className="flex space-x-3">
                  <Button
                    onClick={() => navigate('/')}
                    className="flex-1"
                  >
                    <Home className="w-5 h-5 mr-2" />
                    Back to Home
                  </Button>
                  
                  <Button
                    onClick={() => navigate('/gallery')}
                    variant="outline"
                    className="flex-1"
                  >
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    Continue Shopping
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess; 